import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Users, Zap, Target, Heart, Footprints } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { NeonButton } from "@/components/ui/NeonButton";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { NeonCard, NeonCardContent } from "@/components/ui/NeonCard";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";

const Index = () => {
  const { user } = useAuth();
  
  return (
    <>
      <MarketingHeader />
      <div id="main" className="min-h-screen gradient-dark overflow-hidden pt-24">

      {/* Hero Section - Cinematic */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-8 md:pt-12">
        {/* Hero background image with overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Athlete training"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-bg/95 via-dark-bg/80 to-dark-bg"></div>
        </div>
        
        {/* Animated background particles */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Neon light streaks (center-framed) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Left stripe framing the hero headline */}
          <div
            className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/25 to-transparent animate-pulse"
            style={{ left: 'calc(50% - 240px)' }}
          />
          {/* Right stripe framing the hero headline */}
          <div
            className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent animate-pulse"
            style={{ left: 'calc(50% + 240px)', animationDelay: '0.5s' }}
          />
        </div>

        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-5xl mx-auto space-y-8">
            {/* Main headline with kinetic animation */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="text-6xl md:text-8xl font-black text-white leading-none tracking-tighter"
            >
              TRAIN <span className="text-primary">SMARTER</span>.<br />
              LEVEL <span className="text-primary">UP</span>.
            </motion.h1>
            
            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="text-lg md:text-xl text-gray-light max-w-3xl mx-auto leading-relaxed"
            >
              Frequent Health creates personalized plans for strength, endurance, and recovery – built for your life, at your speed and powered by proprietary AI.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <Link to="/signup">
                <NeonButton className="px-8 py-4 text-sm animate-pulse-glow text-black">
                  Train Smarter Now
                  <ArrowRight className="ml-2 w-6 h-6" />
                </NeonButton>
              </Link>
              <Link to="/how-it-works">
                <NeonButton variant="secondary" className="px-8 py-4 text-sm">
                  The Frequent Health Difference
                </NeonButton>
              </Link>
            </motion.div>

            
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-primary rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>
      {/* Problem → Solution Section */}
      <section className="relative py-32 bg-dark-card border-b border-primary/10">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            {/* Problem - with image */}
            <div className="space-y-6 animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden h-80 mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1546483875-ad9014c88eba?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0"
                  alt="Woman training with dumbbells"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-dark-card/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="inline-block px-4 py-2 bg-white backdrop-blur-sm border border-white rounded-full text-black text-sm font-bold uppercase tracking-wider">
                    The Problem
                  </div>
                </div>
              </div>
              <h2 className="text-5xl font-black text-white leading-tight">
                You <span className="text-primary">train</span> hard — with no results.
              </h2>
              <p className="text-xl text-gray-light leading-relaxed">
                Generalized workout programs and impersonal gyms do not account for your unique needs, goals, recovery patterns, energy levels, or stressors. You overtrain, underperform, and end up frustrated.
              </p>
            </div>

            {/* Solution - with image */}
            <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="relative rounded-2xl overflow-hidden h-80 mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1532029837206-abbe2b7620e3?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="Athlete performing overhead press"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-dark-card/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="inline-block px-4 py-2 bg-primary/80 backdrop-blur-sm border border-primary rounded-full text-black text-sm font-bold uppercase tracking-wider">
                    The Breakthrough
                  </div>
                </div>
              </div>
              <h2 className="text-5xl font-black text-white leading-tight">
                Our AI adapts a <span className="text-primary">personalized program</span> to you in real-time.
              </h2>
              <p className="text-xl text-gray-light leading-relaxed">
                Frequent Health takes 20+ years of expert knowledge and harnesses proprietary AI to learn your patterns and create a program that adapts and grows along with your needs and changing goals.
              </p>
              <Link to="/signup">
                <Button size="lg" className="mt-4 font-bold uppercase">
                  Experience the Difference
                  <Zap className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features - The AI Edge */}
      <section className="relative py-32 bg-dark-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-card/50 to-transparent pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <SectionHeader eyebrow="Frequent Health’s AI Edge" title={<>
            Train with intelligence.<br />Not just <span className="text-primary">intensity</span>.
          </>} />

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Feature 1 */}
            <NeonCard className="group">
              <NeonCardContent className="space-y-6">
                <div className="relative">
                  <div className="w-20 h-20 gradient-primary rounded-2xl flex items-center justify-center mx-auto shadow-neon-glow group-hover:shadow-neon-glow-strong transition-all">
                    <Target className="w-10 h-10 text-black" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white text-center">Frequent Health’s Coach</h3>
                <p className="text-gray-light text-center leading-relaxed">
                  Your AI Coach knows YOU, trains along with you, understands your form, fatigue, progress, and adapts until you achieve success.
                </p>
              </NeonCardContent>
            </NeonCard>

            {/* Feature 2 */}
            <NeonCard className="group" style={{ animationDelay: '0.1s' }}>
              <NeonCardContent className="space-y-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-[#b000ff] rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-all">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white text-center">Training with Intelligence</h3>
                <p className="text-gray-light text-center leading-relaxed">
                  Frequent Health tracks and optimizes your fitness routine using proprietary AI and decades of experience.
                </p>
              </NeonCardContent>
            </NeonCard>

            {/* Feature 3 */}
            <NeonCard className="group" style={{ animationDelay: '0.2s' }}>
              <NeonCardContent className="space-y-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-[#004eff] rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-all">
                    <Footprints className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white text-center">Personalized-Guided Programs</h3>
                <p className="text-gray-light text-center leading-relaxed">
                  Your coach monitors your progress and adapts your plan to keep improving weekly.
                </p>
              </NeonCardContent>
            </NeonCard>
          </div>
        </div>
      </section>

      {/* Proof & Community - Reviews only */}
      <section className="relative py-32 bg-dark-bg border-b border-primary/10">
        <div className="container mx-auto px-6">
          <SectionHeader
            eyebrow="Proof & Community"
            title={
              <>
                User <span className="text-primary">Results</span> Seen, Heard &amp;
                {" "}Shared
              </>
            }
            subtitle="Stories from people training smarter with Frequent Health"
          />

          {/* Reviews grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              {
                quote:
                  "I’m very pleased with the site. It was tailored to me personally, and spot on. It is thorough, and it feels very one-on-one.",
                name: "Teddy A, 53",
              },
              {
                quote:
                  "This is a massive upgrade compared to tracking all my workouts in my phone. The videos are super helpful as well, thorough and easy to use.",
                name: "Will H, 28",
              },
              {
                quote:
                  "It’s helpful to have the videos and the descriptions of how to do the exercises. I also like being able to check the exercises off as I complete them.",
                name: "Ali C, 37",
              },
              {
                quote:
                  "The warm-ups are phenomenal and get me prepared. The workouts are challenging and not run of the mill. They address exactly what I need for the upcoming immediate and long term goals.",
                name: "Mike L, 52",
              },
              {
                quote:
                  "Frequent Health has changed my view on using technology for guided health plans. The customized plan, plus clear videos, and consistent interaction confirms that my plan is customized for my needs and goals and can adapt as I get stronger or my goals change.",
                name: "Christine H, 46",
              },
              {
                quote: "The videos are a huge bonus. I continually refer back to them.",
                name: "Jake P, 37",
              },
              {
                quote:
                  "The Frequent Health app and workout programs are exceptional. I’ve been using them for about a year, and while I noticed results early on, what’s impressed me most is how sustainable they’ve been. At 40, I feel stronger, more mobile, and more balanced than ever.\n\nI realized I’d been overtraining certain muscle groups. which explained my back and knee pain. while neglecting the mobility and stability work that actually drives long-term progress. Since following the program, I’ve been able to do things I honestly didn’t expect: I rode waves on a surfboard again after 20 years, and I even went ice skating with a level of balance I’ve never had before.\n\nI’m doing more pull-ups, my lower back pain is virtually gone, and I feel like I’m building a body that’s not just stronger, but more resilient. If you want a program that delivers real results and keeps you improving month after month, Frequent Health is the one.",
                name: "Rich S, 40",
              },
            ].map((t, idx) => (
              <div
                key={idx}
                className={`relative group ${
                  idx === 6 ? "md:col-span-2 lg:col-span-3" : ""
                }`}
              >
                <div className="absolute inset-x-0 -top-1 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70" />
                <div className="bg-dark-card/80 backdrop-blur-sm border border-primary/15 rounded-2xl p-8 shadow-neon-glow/20 h-full flex flex-col">
                  <p className="text-white text-lg leading-relaxed flex-1 whitespace-pre-line">“{t.quote}”</p>
                  <div className="mt-6 text-gray-light text-sm">— {t.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-32 bg-dark-bg">
        <div className="container mx-auto px-6">
          <SectionHeader
            eyebrow="Frequent Health Works"
            title={
              <>
                Train smarter with an <span className="text-primary">Intelligent System</span>
              </>
            }
          />

          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-dark-card border-2 border-primary/30 rounded-2xl overflow-hidden hover:border-primary hover:shadow-neon-glow transition-all group">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1588528402605-1f9d0eb7a6d6?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0"
                    alt="Woman sitting on gym equipment"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-card to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center text-3xl font-black text-black shadow-neon-glow">
                      01
                    </div>
                  </div>
                </div>
                <div className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-white">Intelligent Assessment</h3>
                  <p className="text-gray-light leading-relaxed">
                    Easily assess your goals, fitness level, limitations, and motivations.
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="hidden md:block absolute top-1/2 -right-6 text-primary text-4xl z-10">→</div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-dark-card border-2 border-primary/30 rounded-2xl overflow-hidden hover:border-primary hover:shadow-neon-glow transition-all group">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1549060279-7e168fcee0c2?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0"
                    alt="Person about to lift a barbell"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-card to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center text-3xl font-black text-black shadow-neon-glow">
                      02
                    </div>
                  </div>
                </div>
                <div className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-white">Customized Pathway</h3>
                  <p className="text-gray-light leading-relaxed">
                    Expert-designed programs that adapt in real time. Featuring structured training blocks with rapid session-by-session calibration based on your data.
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="hidden md:block absolute top-1/2 -right-6 text-primary text-4xl z-10">→</div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-dark-card border-2 border-primary/30 rounded-2xl overflow-hidden hover:border-primary hover:shadow-neon-glow transition-all group">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1554344728-77cf90d9ed26?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0"
                    alt="Man exercising in a dim gym"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-card to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center text-3xl font-black text-black shadow-neon-glow">
                      03
                    </div>
                  </div>
                </div>
                <div className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-white">Measurable Change</h3>
                  <p className="text-gray-light leading-relaxed">
                    AI-assisted audits of weekly progress to adjust your pace and targets.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Teaser CTA to full page */}
          <div className="text-center mt-16">
            <Link to="/how-it-works">
              <NeonButton variant="secondary" className="px-8 py-4 text-base">Full Frequent Health Demonstration</NeonButton>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 bg-dark-bg overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://xnjrgbjrqhbqsfocowbt.supabase.co/storage/v1/object/public/marketing/luke-witter-k47w6BeapCs-unsplash.jpg"
            alt="Training motivation"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-dark-bg/90 to-dark-bg/80"></div>
        </div>
        
        {/* Glowing circle pulse */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl animate-pulse-glow"></div>
          <div className="absolute w-[42rem] h-[42rem] rounded-full border border-primary/20"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-6xl md:text-7xl font-black text-white leading-tight">
              <span className="text-primary">Train Smarter</span> Today.
            </h2>
            <p className="text-2xl text-gray-light">
              Expert-designed, AI-adaptive programs creating your pathway to fitness.
            </p>
            <Link to="/signup">
              <NeonButton className="px-8 py-4 text-base animate-pulse-glow mt-6">
                Get Started
                <ArrowRight className="ml-3 w-6 h-6" />
              </NeonButton>
            </Link>
          </div>
        </div>
      </section>

      {/* Using global footer outside main */}
      </div>
      <MarketingFooter />
    </>
  );
};

export default Index;
